/**
 * Remise sur l'appareil — la notification qui arrive téléphone verrouillé, application
 * fermée (docs/02-fonctionnel.md §10).
 *
 * C'est le seul canal de Repère qui s'affiche **sans que le parent ait rien ouvert**.
 * Cela ne change pas la règle du §10, cela la durcit : ce qui s'affiche sur un écran
 * verrouillé est lu par qui passe à côté, et parfois par l'enfant. Le message porte donc
 * exactement ce que porte l'e-mail — un fait survenu, jamais son contenu, jamais son
 * auteur, jamais son sens.
 *
 * Le libellé vit ici, dans le noyau, et non dans le client de remise : c'est une règle
 * produit, pas un détail de transport, et elle doit se vérifier sans réseau.
 */
/** Transports natifs. Ce sont les deux seuls qui remettent application éteinte. */
export const PUSH_PLATFORMS = ['android', 'ios'];
/**
 * Canal Android des nouveautés.
 *
 * Android 8 et suivants n'affichent une notification que si son canal existe **déjà** sur
 * l'appareil. L'identifiant est donc partagé : l'application le crée à son démarrage,
 * l'API le nomme dans chaque envoi. Deux valeurs qui divergent produiraient une
 * notification que le système accepte et n'affiche jamais — la panne la plus difficile à
 * voir, puisque l'envoi, lui, réussit.
 */
export const PUSH_ANDROID_CHANNEL = 'dossier';
export const PUSH_ANDROID_CHANNEL_LABEL = 'Nouveautés du dossier';
/**
 * Canal Android de l'alerte urgente — **séparé, et c'est tout l'objet**.
 *
 * L'importance d'un canal appartient à celui qui reçoit : Android lui laisse la baisser,
 * canal par canal, depuis les réglages du téléphone. C'est une bonne chose, et c'est
 * exactement ce qui interdit de faire passer l'alerte urgente par le canal des nouveautés :
 * un parent lassé d'être prévenu de chaque dépense baisse « Nouveautés du dossier », et
 * fait taire du même geste la seule notification que le produit se permet d'imposer. Il ne
 * l'apprendrait que le jour où elle compte.
 *
 * Deux canaux, donc deux réglages : on peut rendre le quotidien silencieux **et** rester
 * joignable en cas d'urgence. C'est aussi la seule façon d'obtenir le passage outre le
 * mode « Ne pas déranger », qui se demande par canal.
 *
 * L'identifiant est partagé comme celui du canal ordinaire : la coque le crée au démarrage,
 * l'API le nomme dans chaque envoi. Deux valeurs qui divergent produiraient une
 * notification que le système accepte et n'affiche jamais.
 */
export const PUSH_ANDROID_CHANNEL_URGENCE = 'urgence';
export const PUSH_ANDROID_CHANNEL_URGENCE_LABEL = 'Alertes urgentes';
/**
 * Le message d'un lot remis sur l'appareil.
 *
 * `resume` vient de `summarize` : la même énumération que l'e-mail, donc la même
 * fermeture de la liste des faits notifiables. Rien n'est ajouté ici — pas de nom de
 * dossier, pas de nom d'enfant, pas d'extrait.
 */
export function pushDigest(resume) {
    return {
        title: 'Du nouveau dans votre dossier',
        body: `Votre dossier Repère contient ${resume}.`,
    };
}
/**
 * Le message d'un lot qui porte une alerte urgente.
 *
 * Il **dit qu'il est urgent**, et rien de plus. Ce n'est pas une entorse à la règle du §10
 * — le niveau n'est pas le contenu : il ne dit ni ce qui se passe, ni qui l'a déposée, ni
 * sur quel enfant. Mais le taire serait pire que de le dire : une notification qui traverse
 * le mode « Ne pas déranger » à deux heures du matin en annonçant « du nouveau dans votre
 * dossier » se lit comme un dérangement gratuit, et le parent coupe le canal. Elle ne
 * sonnerait plus la fois suivante.
 *
 * Le corps reste `resume`, la même énumération fermée que l'e-mail et que le digest : le
 * lot qui déclenche l'urgence peut en porter d'autres, et les taire ferait disparaître des
 * faits que le parent est en train d'être réveillé pour.
 */
export function pushAlerteUrgente(resume) {
    return {
        title: 'Alerte urgente',
        body: `Votre dossier Repère contient ${resume}. Ouvrez l’application.`,
    };
}
//# sourceMappingURL=push.js.map